import React, { useState, useEffect } from 'react'
import { apiClient } from '../../../services/apiClient'
import Modal from '../common/Modal'
import './KnowledgeModal.css'

function KnowledgeModal({ isOpen, onClose, onConfigUpdate }) {
  
  // Local state
  const [activeTab, setActiveTab] = useState('bedrock')
  const [bedrockConfig, setBedrockConfig] = useState({
    is_configured: false,
    knowledge_base_id: '',
    region: 'us-east-1',
    api_key_configured: false,
    has_credentials: false,
    client_status: 'not_initialized'
  })
  const [loading, setLoading] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testQuery, setTestQuery] = useState('')
  const [configForm, setConfigForm] = useState({
    knowledge_base_id: '',
    region: 'us-east-1',
    bearer_token: ''
  })
  const [configMessage, setConfigMessage] = useState(null)

  const closeModal = () => {
    onClose()
  }

  // Check authentication status
  const checkAuthentication = () => {
    const authToken = localStorage.getItem('cognito_id_token')
    const idToken = localStorage.getItem('cognito_id_token')
    const refreshToken = localStorage.getItem('cognito_refresh_token')
    const sessionExpiry = localStorage.getItem('cognito_session_expiry')
    
    console.log('Authentication check:', {
      hasAccessToken: !!authToken,
      hasIdToken: !!idToken,
      hasRefreshToken: !!refreshToken,
      sessionExpiry: sessionExpiry,
      allLocalStorageKeys: Object.keys(localStorage).filter(key => key.includes('cognito')),
      tokenPreview: authToken ? authToken.substring(0, 20) + '...' : 'No token'
    })
    
    if (!authToken) {
      console.error('No authentication token found. User needs to log in.')
      setTestResult({
        success: false,
        error: 'Authentication required. Please log in to access Bedrock configuration.'
      })
      return false
    }
    
    // Check if token is expired
    if (sessionExpiry) {
      const expiryTime = new Date(sessionExpiry).getTime()
      const currentTime = new Date().getTime()
      if (currentTime > expiryTime) {
        console.error('Authentication token has expired.')
        setTestResult({
          success: false,
          error: 'Authentication token has expired. Please log in again.'
        })
        return false
      }
    }
    
    return true
  }

  // Load Bedrock configuration on modal open
  useEffect(() => {
    if (isOpen) {
      if (checkAuthentication()) {
        loadBedrockConfig()
      }
    }
  }, [isOpen])

  const loadBedrockConfig = async () => {
    try {
      setLoading(true)
      console.log('Loading Bedrock configuration...')
      
      // Check if user is authenticated
      const authToken = localStorage.getItem('cognito_id_token')
      console.log('Auth token available:', !!authToken)
      if (authToken) {
        console.log('Token preview:', authToken.substring(0, 20) + '...')
      }
      
      // apiClient.request() already returns parsed JSON, not a Response object
      const config = await apiClient.request('/bedrock/config', {
        method: 'GET'
      })
      
      console.log('Loaded config:', config)
      setBedrockConfig(config)
      setConfigForm({
        knowledge_base_id: config.knowledge_base_id || '',
        region: config.region || 'us-east-1',
        bearer_token: '' // Never show the actual token for security
      })
    } catch (error) {
      console.error('Failed to load Bedrock configuration:', error)
      setTestResult({
        success: false,
        error: error.message || 'Failed to load configuration'
      })
    } finally {
      setLoading(false)
    }
  }

  const testBedrockConnection = async () => {
    try {
      setLoading(true)
      setTestResult(null)
      
      // Check if user is authenticated
      const authToken = localStorage.getItem('cognito_id_token')
      console.log('Testing Bedrock connection...')
      console.log('Auth token available:', !!authToken)
      
      // Don't override headers - let apiClient handle authorization
      const result = await apiClient.request('/bedrock/test', {
        method: 'POST',
        body: JSON.stringify({})
      })
      
      console.log('Test connection result:', result)
      setTestResult(result)
    } catch (error) {
      console.error('Failed to test Bedrock connection:', error)
      setTestResult({
        success: false,
        error: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  const updateBedrockConfig = async () => {
    try {
      setLoading(true)
      setConfigMessage(null)
      
      // Validate required fields
      if (!configForm.knowledge_base_id || !configForm.knowledge_base_id.trim()) {
        setConfigMessage({
          type: 'error',
          title: 'Configuration Error',
          message: 'Knowledge Base ID is required.'
        })
        setLoading(false)
        return
      }
      
      if (!configForm.region || !configForm.region.trim()) {
        setConfigMessage({
          type: 'error',
          title: 'Configuration Error',
          message: 'AWS Region is required. Please select a valid region.'
        })
        setLoading(false)
        return
      }
      
      if (!configForm.bearer_token || !configForm.bearer_token.trim()) {
        setConfigMessage({
          type: 'error',
          title: 'Configuration Error',
          message: 'Bedrock API Key (Bearer Token) is required.'
        })
        setLoading(false)
        return
      }
      
      // Prepare request body with validated and trimmed values
      const requestBody = {
        knowledge_base_id: configForm.knowledge_base_id.trim(),
        region: configForm.region.trim(),
        bearer_token: configForm.bearer_token.trim()
      }
      
      console.log('🔄 Updating Bedrock configuration with:', {
        knowledge_base_id: requestBody.knowledge_base_id,
        region: requestBody.region,
        bearer_token: '***'
      })
      
      // Check authentication before making request
      if (!checkAuthentication()) {
        setLoading(false)
        return
      }
      
      // Double-check token availability right before the request
      const authToken = localStorage.getItem('cognito_id_token')
      console.log('Token check before POST request:', {
        hasToken: !!authToken,
        tokenLength: authToken ? authToken.length : 0,
        tokenPreview: authToken ? authToken.substring(0, 20) + '...' : 'No token'
      })
      
      // Don't override headers - let apiClient handle authorization
      const result = await apiClient.request('/bedrock/config', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
      
      console.log('Update result:', result)
      
      if (result.success) {
        await loadBedrockConfig() // Reload configuration
        setConfigMessage({
          type: 'success',
          title: 'Configuration Updated Successfully',
          message: 'Your Bedrock Knowledge Base configuration has been saved and is ready to use.'
        })
        // Notify parent component of config update
        if (onConfigUpdate) {
          onConfigUpdate()
        }
      } else {
        // Show detailed error message with suggestions if available
        let errorMessage = result.error || 'Failed to update configuration. Please check your settings and try again.'
        
        // Get suggestions from result or test_result details
        let suggestions = result.suggestions || []
        if (result.test_result && result.test_result.details && result.test_result.details.suggestions) {
          suggestions = result.test_result.details.suggestions
        }
        
        // Format error message with suggestions
        if (suggestions.length > 0) {
          errorMessage += '\n\nSuggestions:\n' + suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n')
        }
        
        // Add test result details if available
        if (result.test_result && result.test_result.details) {
          const details = result.test_result.details
          if (details.error_code) {
            errorMessage += `\n\nError Code: ${details.error_code}`
          }
          if (details.has_env_credentials !== undefined) {
            errorMessage += `\n\nAWS Credentials in Environment: ${details.has_env_credentials ? 'Yes' : 'No'}`
          }
        }
        
        setConfigMessage({
          type: 'error',
          title: 'Configuration Failed',
          message: errorMessage
        })
        
        // Also set test result to show detailed error
        if (result.test_result) {
          setTestResult(result.test_result)
        }
      }
    } catch (error) {
      console.error('Failed to update Bedrock configuration:', error)
      
      // Extract error message from different error formats
      let errorMessage = 'Failed to update configuration. Please check your connection and try again.'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      setConfigMessage({
        type: 'error',
        title: 'Configuration Error',
        message: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  const handleConfigChange = (field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleTestQuery = async () => {
    if (!testQuery.trim()) return
    
    try {
      setLoading(true)
      setTestResult(null)
      
      // Don't override headers - let apiClient handle authorization
      const result = await apiClient.request('/bedrock/query', {
        method: 'POST',
        body: JSON.stringify({
          query: testQuery.trim(),
          max_results: 5,
          use_generate: false
        })
      })
      
      setTestResult(result)
    } catch (error) {
      console.error('Failed to query Bedrock:', error)
      setTestResult({
        success: false,
        error: error.message || 'Failed to query Bedrock. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={closeModal}
      className="knowledge-modal"
    >
      <div className="knowledge-modal-content">
        <div className="knowledge-modal-header">
          <h2><i className="fas fa-database"></i> Knowledge Base Management</h2>
          <button className="close-btn" onClick={closeModal}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="knowledge-tabs">
          <button 
            className={`kb-tab-btn ${activeTab === 'bedrock' ? 'active' : ''}`}
            onClick={() => setActiveTab('bedrock')}
          >
            <i className="fas fa-cog"></i> Configuration
          </button>
          <button 
            className={`kb-tab-btn ${activeTab === 'testing' ? 'active' : ''}`}
            onClick={() => setActiveTab('testing')}
          >
            <i className="fas fa-flask"></i> Testing
          </button>
        </div>
        
        <div className="knowledge-content">
          {/* Testing Tab */}
          {activeTab === 'testing' && (
          <div className="knowledge-section">
              <h3><i className="fas fa-flask"></i> Test Bedrock Knowledge Base</h3>
              
              {!bedrockConfig.is_configured ? (
                <div className="testing-not-configured">
                  <div className="not-configured-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <h4>Configuration Required</h4>
                  <p>Please configure your Bedrock Knowledge Base in the Configuration tab before testing.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveTab('bedrock')}
                  >
                    <i className="fas fa-cog"></i> Go to Configuration
              </button>
            </div>
              ) : (
                <div className="testing-interface">
                  <div className="test-query-section">
                    <label htmlFor="test-query">Test Query:</label>
                    <textarea
                      id="test-query"
                      value={testQuery}
                      onChange={(e) => setTestQuery(e.target.value)}
                      placeholder="Enter your question to test the Bedrock Knowledge Base..."
                      className="test-query-input"
                      rows="4"
                    />
                    <button 
                      className="btn btn-primary test-query-btn"
                      onClick={handleTestQuery}
                      disabled={loading || !testQuery.trim()}
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Testing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Test Query
                        </>
                      )}
                    </button>
                  </div>
                  
                  {testResult && (
                    <div className="test-result-section">
                      <h4><i className="fas fa-robot"></i> Bedrock Response</h4>
                      <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                        {testResult.success ? (
                          <div className="response-content">
                            {/* Show answer if using retrieve_and_generate */}
                            {testResult.answer && (
                              <div className="response-text">
                                <h5>Answer:</h5>
                                <p>{testResult.answer}</p>
                              </div>
                            )}
                            
                            {/* Show results if using retrieve */}
                            {testResult.results && testResult.results.length > 0 && (
                              <div className="response-results">
                                <h5>Results ({testResult.total_results || testResult.results.length}):</h5>
                                {testResult.results.map((result, index) => (
                                  <div key={index} className="result-item">
                                    <div className="result-score">
                                      Score: {(result.score * 100).toFixed(1)}%
                                    </div>
                                    <div className="result-content">
                                      {result.content}
                                    </div>
                                    {result.source?.uri && (
                                      <div className="result-source">
                                        <i className="fas fa-file-alt"></i>
                                        <small>{result.source.uri}</small>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Show sources if available */}
                            {testResult.sources && testResult.sources.length > 0 && (
                              <div className="response-sources">
                                <h5>Sources:</h5>
                                <ul>
                                  {testResult.sources.map((source, index) => (
                                    <li key={index}>
                                      <i className="fas fa-file-alt"></i>
                                      {source.uri || source.source_type || `Source ${index + 1}`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* If no results */}
                            {!testResult.answer && (!testResult.results || testResult.results.length === 0) && (
                              <div className="no-results">
                                <i className="fas fa-info-circle"></i>
                                <p>No results found for your query.</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="error-content">
                            <i className="fas fa-exclamation-circle"></i>
                            <span>{testResult.error || 'An error occurred while querying Bedrock.'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bedrock Knowledge Base Tab */}
          {activeTab === 'bedrock' && (
            <div className="knowledge-section">
              <h3><i className="fab fa-aws"></i> Amazon Bedrock Knowledge Base</h3>
              
              {/* Configuration Message */}
              {configMessage && (
                <div className="config-message">
                  <div className={`result-card ${configMessage.type}`}>
                    <div className="result-header">
                      <i className={`fas ${configMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                      <span>{configMessage.title}</span>
                    </div>
                    <div className="result-content">
                      <p>{configMessage.message}</p>
                    </div>
                  </div>
                </div>
              )}
          
              {/* Configuration Form */}
              <div className="bedrock-config">
                <h4><i className="fas fa-edit"></i> Configuration</h4>
                <div className="config-form">
                  <div className="kb-form-group">
                    <label>Knowledge Base ID:</label>
                    <input
                      type="text"
                      value={configForm.knowledge_base_id}
                      onChange={(e) => handleConfigChange('knowledge_base_id', e.target.value)}
                      placeholder="Enter your Bedrock Knowledge Base ID"
                      className="config-input"
                    />
                    <small>Example: ABCD1234EF</small>
                  </div>
                  <div className="kb-form-group">
                    <label>AWS Region:</label>
                    <select
                      value={configForm.region}
                      onChange={(e) => handleConfigChange('region', e.target.value)}
                      className="config-select"
                    >
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-east-2">US East (Ohio)</option>
                      <option value="us-west-1">US West (N. California)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">Europe (Ireland)</option>
                      <option value="eu-central-1">Europe (Frankfurt)</option>
                      <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                      <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                    </select>
                  </div>
                  
                  {/* Bedrock API Key */}
                  <div className="kb-form-group">
                    <label>Amazon Bedrock API Key:</label>
                    <input
                      type="password"
                      value={configForm.bearer_token}
                      onChange={(e) => handleConfigChange('bearer_token', e.target.value)}
                      placeholder="ABSKam9hbkBjb25zaWxpZW50bGFicy5jb20..."
                      className="config-input"
                    />
                    <small>
                      Required. The API key will be used in the x-amz-api-key header. 
                      AWS credentials should be configured in the server environment (environment variables, IAM role, or AWS profile).
                    </small>
                  </div>
                  
                  <div className="form-actions">
                    <button
                      onClick={updateBedrockConfig}
                      disabled={loading || !configForm.knowledge_base_id.trim() || !configForm.bearer_token.trim()}
                      className="config-btn primary"
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Update Configuration
                        </>
                      )}
                    </button>
                    <button
                      onClick={testBedrockConnection}
                      disabled={loading || !bedrockConfig.is_configured}
                      className="config-btn secondary"
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Testing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plug"></i>
                          Test Connection
                        </>
                      )}
                    </button>
                  </div>
            </div>
          </div>
          
              {/* Test Results */}
              {testResult && (
                <div className="test-results">
                  <h4><i className="fas fa-flask"></i> Connection Test Results</h4>
                  <div className={`result-card ${testResult.success ? 'success' : 'error'}`}>
                    <div className="result-header">
                      <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                      <span>{testResult.success ? 'Connection Successful' : 'Connection Failed'}</span>
                    </div>
                    <div className="result-content">
                      {testResult.success ? (
                        <p>{testResult.message}</p>
                      ) : (
                        <>
                          <p><strong>Error:</strong> {testResult.error}</p>
                          {testResult.details?.suggestions && (
                            <div className="suggestions">
                              <strong>Suggestions:</strong>
                              <ul>
                                {testResult.details.suggestions.map((suggestion, index) => (
                                  <li key={index}>{suggestion}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
            </div>
          </div>
              )}

              {/* Setup Instructions */}
              <div className="setup-instructions">
                <h4><i className="fas fa-info-circle"></i> Setup Instructions</h4>
                <div className="instruction-steps">
                  <div className="step">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <strong>Create Knowledge Base</strong>
                      <p>Create a Knowledge Base in Amazon Bedrock console and note the ID.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <strong>Get API Key</strong>
                      <p>Generate or obtain your Bedrock API Key from the AWS console.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <strong>Configure Settings</strong>
                      <p>Enter your Knowledge Base ID, select the correct AWS region, and provide your API Key.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <strong>T est Connection</strong>
                      <p>Click "Test Connection" to verify your configuration is working correctly.</p>
                    </div>
            </div>
            </div>
            </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}


export default KnowledgeModal